import {type FormEvent, type ReactNode, useEffect, useMemo, useState} from 'react';
import {
    type MarketplaceAdminTheme,
    createAdminMarketplaceTheme,
    deleteAdminMarketplaceTheme,
    getAdminMarketplaceCustomThemes,
    getAdminMarketplaceSlugAvailability,
    getAdminMarketplaceTheme,
    getAdminMarketplaceThemes,
    importAdminMarketplaceCustomTheme,
    setAdminMarketplacePricing,
    setAdminMarketplacePublished,
    uploadAdminMarketplaceVersion
} from '@/api/marketplace';
import {Select} from '@/components/ui/Select';
import {formatNumber} from '@tryghost/shade/utils';
import {useDebounce} from '@/hooks/useDebounce';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

function cents(value: number | null) {
    if (value === null) {
        return 'Free';
    }
    const whole = Math.floor(value / 100);
    return `$${formatNumber(whole)}.${(value % 100).toString().padStart(2, '0')}`;
}

function countLabel(value: number, singular: string) {
    return `${formatNumber(value)} ${value === 1 ? singular : `${singular}s`}`;
}

function PlusIcon() {
    return <svg aria-hidden="true" fill="none" height="18" viewBox="0 0 24 24" width="18"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeLinecap="round" strokeWidth="2"/></svg>;
}

function PackageIcon() {
    return <svg aria-hidden="true" fill="none" height="24" viewBox="0 0 24 24" width="24"><path d="m4 7 8-4 8 4-8 4-8-4Z" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.7"/><path d="M4 7v10l8 4 8-4V7M12 11v10" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.7"/></svg>;
}

function SearchIcon() {
    return <svg aria-hidden="true" fill="none" height="16" viewBox="0 0 24 24" width="16"><circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8"/><path d="m16 16 4 4" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8"/></svg>;
}

function UploadIcon() {
    return <svg aria-hidden="true" fill="none" height="24" viewBox="0 0 24 24" width="24"><path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7"/><path d="M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" stroke="currentColor" strokeLinecap="round" strokeWidth="1.7"/></svg>;
}

function CheckIcon() {
    return <svg aria-hidden="true" fill="none" height="16" viewBox="0 0 24 24" width="16"><path d="m6 12 4 4 8-9" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/></svg>;
}

function ChevronIcon() {
    return <svg aria-hidden="true" fill="none" height="16" viewBox="0 0 24 24" width="16"><path d="m9 6 6 6-6 6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8"/></svg>;
}

function ExternalIcon() {
    return <svg aria-hidden="true" fill="none" height="14" viewBox="0 0 24 24" width="14"><path d="M14 5h5v5M19 5l-8 8" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8"/><path d="M19 14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h4" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8"/></svg>;
}

function TrashIcon() {
    return <svg aria-hidden="true" fill="none" height="16" viewBox="0 0 24 24" width="16"><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8"/></svg>;
}

function StatusBadge({status}: {status: MarketplaceAdminTheme['status']}) {
    return <span className={`marketplace-status marketplace-status--${status}`}><span />{status}</span>;
}

function themeSlug(value: string) {
    return value
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 191)
        .replace(/-+$/g, '');
}

function Field({label, hint, error, className = '', children}: {label: string; hint?: string; error?: string; className?: string; children: ReactNode}) {
    return (
        <label className={`marketplace-field${className ? ` ${className}` : ''}`}>
            <span>{label}</span>
            {children}
            {error ? <small className="marketplace-field-error" role="alert">{error}</small> : hint && <small>{hint}</small>}
        </label>
    );
}

export function MarketplaceAdminPage() {
    const client = useQueryClient();
    const list = useQuery({queryKey: ['admin', 'marketplace'], queryFn: getAdminMarketplaceThemes});
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const detail = useQuery({queryKey: ['admin', 'marketplace', selectedId], queryFn: () => getAdminMarketplaceTheme(selectedId as string), enabled: Boolean(selectedId)});
    const [showCreate, setShowCreate] = useState(false);
    const [search, setSearch] = useState('');
    const [fileName, setFileName] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [draftSource, setDraftSource] = useState<'custom' | 'zip'>('custom');
    const [selectedCustomThemeId, setSelectedCustomThemeId] = useState('');
    const [draftName, setDraftName] = useState('');
    const [draftSlug, setDraftSlug] = useState('');
    const [draftDescription, setDraftDescription] = useState('');
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
    const [slugConflict, setSlugConflict] = useState(false);
    const debouncedDraftSlug = useDebounce(draftSlug, 300);
    const customThemes = useQuery({
        queryKey: ['admin', 'marketplace', 'custom-themes'],
        queryFn: getAdminMarketplaceCustomThemes,
        enabled: showCreate
    });

    const locallyReservedSlug = useMemo(() => {
        return Boolean(draftSlug && list.data?.some(theme => theme.slug === draftSlug));
    }, [draftSlug, list.data]);

    const slugAvailability = useQuery({
        queryKey: ['admin', 'marketplace', 'slug-availability', debouncedDraftSlug],
        queryFn: () => getAdminMarketplaceSlugAvailability(debouncedDraftSlug),
        enabled: showCreate && Boolean(debouncedDraftSlug) && !locallyReservedSlug,
        retry: false,
        staleTime: 10_000
    });

    const slugCheckIsCurrent = debouncedDraftSlug === draftSlug;
    const slugTaken = locallyReservedSlug || slugConflict || (slugCheckIsCurrent && slugAvailability.data?.available === false);
    const slugCheckFailed = slugCheckIsCurrent && slugAvailability.isError;
    const slugChecking = Boolean(draftSlug) && !locallyReservedSlug && (!slugCheckIsCurrent || slugAvailability.isFetching);

    useEffect(() => {
        if (!selectedId && list.data?.length) {
            setSelectedId(list.data[0].id);
        }
    }, [list.data, selectedId]);

    useEffect(() => {
        setFileName(null);
    }, [selectedId]);

    const filteredThemes = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) {
            return list.data || [];
        }
        return (list.data || []).filter(theme => `${theme.name} ${theme.slug} ${theme.status}`.toLowerCase().includes(query));
    }, [list.data, search]);

    const counts = useMemo(() => {
        const themes = list.data || [];
        return {
            total: themes.length,
            published: themes.filter(theme => theme.status === 'published').length,
            drafts: themes.filter(theme => theme.status === 'draft').length,
            validated: themes.filter(theme => theme.gscan_passed).length
        };
    }, [list.data]);

    const refresh = async (id?: string) => {
        await client.invalidateQueries({queryKey: ['admin', 'marketplace']});
        if (id) {
            await client.invalidateQueries({queryKey: ['admin', 'marketplace', id]});
        }
    };

    const operation = useMutation({
        mutationFn: async (task: () => Promise<unknown>) => task(),
        onSuccess: async () => {
            setError(null);
            setMessage('Marketplace catalog updated.');
            setFileName(null);
            await refresh(selectedId || undefined);
        },
        onError: (reason: Error) => {
            setMessage(null);
            setError(reason.message || 'Marketplace operation failed.');
        }
    });

    const removeTheme = useMutation({
        mutationFn: ({id}: {id: string; name: string}) => deleteAdminMarketplaceTheme(id),
        onSuccess: async (_, removed) => {
            const nextTheme = list.data?.find(theme => theme.id !== removed.id);
            setSelectedId(nextTheme?.id || null);
            setError(null);
            setMessage(`“${removed.name}” was permanently deleted.`);
            client.removeQueries({queryKey: ['admin', 'marketplace', removed.id], exact: true});
            await client.invalidateQueries({queryKey: ['admin', 'marketplace']});
        },
        onError: (reason: Error) => {
            setMessage(null);
            setError(reason.message || 'Theme deletion failed.');
        }
    });

    const resetCreateForm = () => {
        setShowCreate(false);
        setDraftSource('custom');
        setSelectedCustomThemeId('');
        setDraftName('');
        setDraftSlug('');
        setDraftDescription('');
        setSlugManuallyEdited(false);
        setSlugConflict(false);
    };

    const selectDraftSource = (source: 'custom' | 'zip') => {
        setDraftSource(source);
        setSelectedCustomThemeId('');
        setDraftName('');
        setDraftSlug('');
        setDraftDescription('');
        setSlugManuallyEdited(false);
        setSlugConflict(false);
    };

    const create = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!draftSlug || slugTaken || slugChecking || slugCheckFailed) {
            return;
        }
        const formElement = event.currentTarget;
        const payload = {slug: draftSlug, name: draftName, description: draftDescription};
        const request = () => (draftSource === 'custom' ?
            importAdminMarketplaceCustomTheme(selectedCustomThemeId, payload) :
            createAdminMarketplaceTheme(payload));

        operation.mutate(() => request().then((theme) => {
            setSelectedId(theme.id);
            resetCreateForm();
            formElement.reset();
            return theme;
        }), {
            onError: (reason) => {
                if ((reason as {code?: string}).code === 'CONFLICT_ERROR') {
                    setSlugConflict(true);
                }
            }
        });
    };

    const selected = detail.data;

    const confirmDelete = () => {
        if (!selected) {
            return;
        }

        const visibilityWarning = selected.status === 'published' ? ' It will immediately disappear from the marketplace.' : '';
        const confirmed = window.confirm(`Delete “${selected.name}”?${visibilityWarning} All uploaded versions and generated previews will be permanently removed. This cannot be undone.`);

        if (confirmed) {
            removeTheme.mutate({id: selected.id, name: selected.name});
        }
    };

    return (
        <div className="marketplace-admin-page studio-content-page studio-admin-page studio-admin-marketplace-page">
            <header className="marketplace-page-header studio-admin-page-heading">
                <div>
                    <h1>Marketplace</h1>
                    <p>Add themes, manage their packages, and control what customers can see.</p>
                </div>
                <button aria-expanded={showCreate} className="marketplace-button marketplace-button--primary" disabled={showCreate} type="button" onClick={() => setShowCreate(true)}>
                    <PlusIcon />New theme
                </button>
            </header>

            {(message || error) && (
                <div className={`marketplace-notice ${error ? 'marketplace-notice--error' : 'marketplace-notice--success'}`} role={error ? 'alert' : 'status'}>
                    <span>{error ? '!' : <CheckIcon />}</span>
                    <p><strong>{error ? 'Unable to complete operation' : 'Changes saved'}</strong>{error || message}</p>
                    <button aria-label="Dismiss message" type="button" onClick={() => {
                        setError(null); setMessage(null);
                    }}>×</button>
                </div>
            )}

            <section aria-label="Marketplace summary" className="marketplace-summary-list">
                <span><strong>{formatNumber(counts.total)}</strong> themes</span>
                <span><strong>{formatNumber(counts.published)}</strong> published</span>
                <span><strong>{formatNumber(counts.drafts)}</strong> drafts</span>
                <span><strong>{formatNumber(counts.validated)}</strong> validated</span>
            </section>

            {showCreate && (
                <section className="marketplace-panel marketplace-create-panel">
                    <div className="marketplace-panel-heading">
                        <div><h2>New marketplace theme</h2><p>Start with an existing custom theme or an upload-ready ZIP.</p></div>
                    </div>
                    <form className="marketplace-create-form" onSubmit={create}>
                        <div aria-label="Theme source" className="marketplace-source-options" role="radiogroup">
                            <button aria-checked={draftSource === 'custom'} className={`marketplace-source-option${draftSource === 'custom' ? ' marketplace-source-option--selected' : ''}`} disabled={!customThemes.isLoading && !customThemes.isError && customThemes.data?.length === 0} role="radio" type="button" onClick={() => selectDraftSource('custom')}>
                                <span className="marketplace-source-option-copy"><strong>Use a custom theme</strong><small>{customThemes.isLoading ? 'Loading themes…' : `${formatNumber(customThemes.data?.length || 0)} available`}</small></span>
                            </button>
                            <button aria-checked={draftSource === 'zip'} className={`marketplace-source-option${draftSource === 'zip' ? ' marketplace-source-option--selected' : ''}`} role="radio" type="button" onClick={() => selectDraftSource('zip')}>
                                <span className="marketplace-source-option-copy"><strong>Upload a ZIP</strong><small>Create an empty listing first</small></span>
                            </button>
                        </div>

                        <div className="marketplace-create-fields">
                            {draftSource === 'custom' && (
                                <Field className="marketplace-field--full" error={customThemes.isError ? 'Could not load custom themes.' : undefined} hint={selectedCustomThemeId ? 'The selected version will be copied and revalidated.' : 'Only validated custom themes are shown.'} label="Custom theme to import">
                                    <Select ariaLabel="Custom theme to import" className="studio-field-control" options={customThemes.data?.map(theme => ({value: theme.id, label: `${theme.name} · ${theme.current_version}`})) || []} placeholder="Choose a custom theme" value={selectedCustomThemeId} width="100%" required onChange={(id) => {
                                        const customTheme = customThemes.data?.find(theme => theme.id === id);
                                        setSelectedCustomThemeId(id);
                                        setSlugConflict(false);
                                        setSlugManuallyEdited(false);
                                        setDraftName(customTheme?.name || '');
                                        setDraftSlug(customTheme ? themeSlug(customTheme.name) : '');
                                        setDraftDescription(customTheme?.description || '');
                                    }} />
                                </Field>
                            )}
                            <Field label="Theme name"><input className="studio-field-control" maxLength={191} name="name" placeholder="e.g. Editorial" value={draftName} required onChange={(event) => {
                                const name = event.target.value;
                                setDraftName(name);
                                setSlugConflict(false);
                                if (!slugManuallyEdited) {
                                    setDraftSlug(themeSlug(name));
                                }
                            }} /></Field>
                            <Field error={slugTaken ? 'This slug is already taken.' : slugCheckFailed ? 'Could not check slug availability. Try again.' : undefined} hint={slugChecking ? 'Checking availability…' : 'Generated from the theme name'} label="Theme slug"><input aria-invalid={slugTaken || slugCheckFailed || undefined} className="studio-field-control" maxLength={191} name="slug" pattern="[a-z0-9-]+" placeholder="editorial-theme" value={draftSlug} required onChange={(event) => {
                                const slug = themeSlug(event.target.value);
                                setDraftSlug(slug);
                                setSlugConflict(false);
                                setSlugManuallyEdited(Boolean(slug) && slug !== themeSlug(draftName));
                            }} /></Field>
                            <Field className="marketplace-field--full" label="Description"><textarea className="studio-field-control" name="description" placeholder="What makes this theme useful?" rows={3} value={draftDescription} onChange={event => setDraftDescription(event.target.value)} /></Field>
                        </div>
                        <div className="marketplace-create-actions">
                            <button className="marketplace-button marketplace-button--secondary" type="button" onClick={resetCreateForm}>Cancel</button>
                            <button className="marketplace-button marketplace-button--primary" disabled={operation.isLoading || (draftSource === 'custom' && !selectedCustomThemeId) || !draftSlug || slugTaken || slugChecking || slugCheckFailed} type="submit">{draftSource === 'custom' ? 'Add to marketplace' : 'Create draft'}</button>
                        </div>
                    </form>
                </section>
            )}

            <div className="marketplace-workspace">
                <aside className="marketplace-panel marketplace-catalog-panel">
                    <div className="marketplace-catalog-header">
                        <div><h2>Themes</h2><span>{countLabel(list.data?.length || 0, 'theme')}</span></div>
                        <div className="marketplace-search"><SearchIcon /><input aria-label="Search catalog" className="studio-field-control" placeholder="Search themes" type="search" value={search} onChange={event => setSearch(event.target.value)} /></div>
                    </div>
                    <div className="marketplace-catalog-list">
                        {list.isLoading && [1, 2, 3].map(item => <div key={item} className="marketplace-catalog-skeleton" />)}
                        {list.isError && <div className="marketplace-inline-error" role="alert"><strong>Catalog failed to load</strong><button type="button" onClick={() => list.refetch()}>Try again</button></div>}
                        {!list.isLoading && !list.isError && filteredThemes.length === 0 && <div className="marketplace-empty-list"><SearchIcon /><span>No themes match “{search}”</span></div>}
                        {filteredThemes.map((theme: MarketplaceAdminTheme) => (
                            <button key={theme.id} aria-pressed={selectedId === theme.id} className={`marketplace-theme-row${selectedId === theme.id ? ' marketplace-theme-row--selected' : ''}`} type="button" onClick={() => setSelectedId(theme.id)}>
                                <span className="marketplace-theme-row-copy"><strong>{theme.name}</strong><small>{theme.is_free ? 'Free' : cents(theme.price_cents)} · {theme.current_version || 'No version'}</small></span>
                                <StatusBadge status={theme.status} />
                                <span className="marketplace-row-chevron"><ChevronIcon /></span>
                            </button>
                        ))}
                    </div>
                </aside>

                <section className="marketplace-panel marketplace-detail-panel">
                    {!selectedId && <div className="marketplace-empty-detail"><span><PackageIcon /></span><h2>Select a theme</h2><p>Choose a catalog entry to manage its package, availability, and publication state.</p></div>}
                    {selectedId && detail.isLoading && <div className="marketplace-detail-loading"><div /><div /><div /></div>}
                    {detail.isError && <div className="marketplace-empty-detail" role="alert"><span>!</span><h2>Theme details failed to load</h2><button className="marketplace-button marketplace-button--secondary" type="button" onClick={() => detail.refetch()}>Try again</button></div>}
                    {selected && (
                        <div className="marketplace-detail-content">
                            <header className="marketplace-theme-header">
                                <div className="marketplace-theme-title">
                                    <div><StatusBadge status={selected.status} />{selected.gscan_passed && <span className="marketplace-validation-badge"><CheckIcon />GScan passed</span>}</div>
                                    <h2>{selected.name}</h2>
                                    <p>{selected.description || 'No catalog description has been added yet.'}</p>
                                    <div className="marketplace-theme-links"><code>{selected.slug}</code>{selected.demo_url && <a href={selected.demo_url} rel="noreferrer" target="_blank">Open demo <ExternalIcon /></a>}</div>
                                </div>
                                <button aria-label={`Delete ${selected.name}`} className="marketplace-button marketplace-button--danger marketplace-delete-theme" disabled={operation.isLoading || removeTheme.isLoading} type="button" onClick={confirmDelete}><TrashIcon />Delete</button>
                            </header>

                            <div className="marketplace-metadata-grid">
                                <div><span>Price</span><strong>{selected.is_free ? 'Free' : cents(selected.price_cents)}</strong></div>
                                <div><span>Version</span><strong>{selected.current_version || 'Not uploaded'}</strong></div>
                                <div><span>Validation</span><strong>{selected.gscan_passed ? 'Passed' : 'Required'}</strong></div>
                            </div>

                            <div className="marketplace-editor-grid">
                                <section className="marketplace-subpanel">
                                    <div className="marketplace-subpanel-heading"><div><h3>Package</h3><p>Upload a ZIP to validate and create a new version.</p></div></div>
                                    {selected.current_artifact_hash && <div className="marketplace-checksum"><span>Current SHA-256</span><code>{selected.current_artifact_hash}</code></div>}
                                    <form className="marketplace-upload-form" onSubmit={(event) => {
                                        event.preventDefault();
                                        const file = new FormData(event.currentTarget).get('file');
                                        if (file instanceof File && file.size) {
                                            operation.mutate(() => uploadAdminMarketplaceVersion(selected.id, file));
                                        }
                                    }}>
                                        <label className="marketplace-dropzone">
                                            <input accept=".zip,application/zip" name="file" type="file" required onChange={event => setFileName(event.target.files?.[0]?.name || null)} />
                                            <span><UploadIcon /></span><strong>{fileName || 'Choose a theme ZIP'}</strong><small>{fileName ? 'Ready for server validation' : 'ZIP only · maximum 50 MB'}</small>
                                        </label>
                                        <button className="marketplace-button marketplace-button--primary marketplace-button--full" disabled={operation.isLoading || removeTheme.isLoading || !fileName} type="submit">Validate and upload</button>
                                    </form>
                                </section>

                                <section className="marketplace-subpanel">
                                    <div className="marketplace-subpanel-heading"><div><h3>Storefront</h3><p>Choose how customers get this theme.</p></div></div>
                                    <form key={selected.id} className="marketplace-pricing-form" onSubmit={(event) => {
                                        event.preventDefault();
                                        const form = new FormData(event.currentTarget);
                                        const isFree = form.get('availability') === 'free';
                                        operation.mutate(() => setAdminMarketplacePricing(selected.id, {
                                            is_free: isFree,
                                            display_price_cents: isFree ? null : Number(form.get('price')),
                                            paykickstart_product_id: isFree ? null : String(form.get('providerProduct'))
                                        }));
                                    }}>
                                        <Field label="Storefront access"><Select ariaLabel="Storefront access" className="studio-field-control" defaultValue={selected.is_free ? 'free' : 'paid'} name="availability" options={[{value: 'free', label: 'Free theme'}, {value: 'paid', label: 'Paid or All Access'}]} width="100%" /></Field>
                                        <Field hint="Customer-facing amount" label="Display price (cents)"><input className="studio-field-control" defaultValue={selected.price_cents || ''} min="1" name="price" placeholder="100" type="number" /></Field>
                                        <Field hint="Configured server-side product" label="Provider product"><input className="studio-field-control" name="providerProduct" placeholder="Product identifier" /></Field>
                                        <button className="marketplace-button marketplace-button--secondary marketplace-button--full" disabled={operation.isLoading || removeTheme.isLoading} type="submit">Save availability</button>
                                    </form>
                                    <div className="marketplace-publish-box">
                                        <div><strong>{selected.status === 'published' ? 'Published in Theme Store' : 'Not visible to customers'}</strong><span>{selected.status === 'published' ? 'Customers can discover this theme.' : 'Complete validation and pricing first.'}</span></div>
                                        <button className={`marketplace-button ${selected.status === 'published' ? 'marketplace-button--danger' : 'marketplace-button--primary'}`} disabled={operation.isLoading || removeTheme.isLoading} type="button" onClick={() => operation.mutate(() => setAdminMarketplacePublished(selected.id, selected.status !== 'published'))}>{selected.status === 'published' ? 'Unpublish' : 'Publish theme'}</button>
                                    </div>
                                </section>
                            </div>

                            <section className="marketplace-history">
                                <div className="marketplace-history-heading"><div><h3>Version history</h3><p>Previously validated packages.</p></div><span>{countLabel(selected.versions?.length || 0, 'version')}</span></div>
                                {!selected.versions?.length && <div className="marketplace-history-empty"><PackageIcon /><span>No validated versions yet</span></div>}
                                {selected.versions?.map(version => (
                                    <div key={version.id} className="marketplace-version-row">
                                        <span className="marketplace-version-icon"><PackageIcon /></span>
                                        <div className="marketplace-version-name"><strong>Version {version.version}</strong><span>{new Date(version.created_at).toLocaleDateString()}</span></div>
                                        <div className="marketplace-version-scan"><span className="marketplace-validation-badge"><CheckIcon />Validated</span><small>{countLabel(version.gscan_result?.warnings?.length || 0, 'warning')}</small></div>
                                        <code>{version.artifact_hash}</code>
                                    </div>
                                ))}
                            </section>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}
